import React, { useEffect, useMemo, useState } from "react";
import useApp from "../../../utils/useApp";
import { Badge, Button, Col, Container, Dropdown, DropdownButton, Form, Row, Spinner } from "react-bootstrap";
import { fetchAllResources, fetchResourceCategories, postResourceCategory } from "../../../services/httpClient";
import { Resource } from "../../../types/resource";
import { unsavedChanges } from "../../../App";
import SaveChangesModal from "../../ui/SaveChangesModal";
import { faClose, faFilter } from "@fortawesome/free-solid-svg-icons";
import OrgResourceCard from "./OrgResourceCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "react-tooltip";
import AddResource from "./AddResource";
import CategoryCard from "./CategoryCard";
import Input from "../../ui/Input";

interface OrgResourcesState {
  setOrgToSave: any;
  getData: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgResources = ({ setOrgToSave, getData, setShowToast, setToastText, setToastBody }: OrgResourcesState) => {
  const { auth, showMenu }: any = useApp();
  const [showAddResource, setShowAddResource] = useState<boolean>(false);
  // const [showConsumables, setShowConsumables] = useState<boolean>(false);
  // const [showNonConsumables, setShowNonConsumables] = useState<boolean>(false);
  const [showCategoryView, setShowCategoryView] = useState<boolean>(false);
  const [showAddCategory, setShowAddCategory] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [all, setAll] = useState<Resource[]>([]);
  const [consumables, setConsumables] = useState<Resource[]>([]);
  const [nonConsumables, setNonConsumables] = useState<Resource[]>([]);
  const [resourceCategories, setResourceCategories] = useState<any[]>([]);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  // const [resourceType, setResourceType] = useState<any>({});
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [categoryFilterName, setCategoryFilterName] = useState<string | null>(null);
  // const [resourceCategory, setResourceCategory] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState<string>("");

  const filterByCategory = <T extends { category_key?: string | null }>(resources: T[], categoryKey?: string | null): T[] => {
    if (!categoryKey) {
      return resources;
    }

    return resources.filter((r) => r.category_key === (categoryKey === "0" ? null : categoryKey));
  };
  const { filteredAll, filteredConsumables, filteredNonConsumables } = useMemo(
    () =>
      categoryFilter
        ? {
            filteredAll: filterByCategory(all, categoryFilter),
            filteredConsumables: filterByCategory(consumables, categoryFilter),
            filteredNonConsumables: filterByCategory(nonConsumables, categoryFilter),
          }
        : { filteredAll: all, filteredConsumables: consumables, filteredNonConsumables: nonConsumables },
    [all, consumables, nonConsumables, categoryFilter],
  );

  useEffect(() => {
    if (getData) {
      getResources();
      getResourceCategories();
    }
  }, [getData]);

  useEffect(() => {
    getResources();
    getResourceCategories();
  }, [auth?.profile]);

  // useEffect(() => {
  //   typeFilter === "all" || typeFilter === "consumable" ? setShowConsumables(true) : setShowConsumables(false);
  //   typeFilter === "all" || typeFilter === "nonconsumable" ? setShowNonConsumables(true) : setShowNonConsumables(false);
  // }, [typeFilter]);

  const getResources = () => {
    setIsLoading(true);
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data;
        const consumables = resources.filter((r: any) => r.type_key === "consumable");
        const nonConsumables = resources.filter((r: any) => r.type_key === "nonconsumable");
        setAll(resources);
        setConsumables(consumables);
        setNonConsumables(nonConsumables);

        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getResourceCategories = async (filterName?: string) => {
    await fetchResourceCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setResourceCategories(cats);
        if (filterName) {
          const newFilter = cats.find((c: any) => c.name === filterName);
          setCategoryFilter(newFilter?.key || null);
          setCategoryFilterName(newFilter?.name || null);
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickAddResourceCategory = (newCategoryName: string) => {
    const newCategory = newCategoryName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    return postResourceCategory({ name: newCategory })
      .then((res: any) => {
        setShowToast(true);
        setToastText("Category Created");
        setToastBody(newCategory);
        setNewCategoryName("");
        setShowAddCategory(false);
        getResourceCategories();
        return { key: newCategoryName, name: newCategory, id: res[0].data.category_id };
      })
      .catch((err: any) => {
        console.log(err);
        throw err;
      });
  };

  const onClickSaveSubmitNewCategory = (e: any) => {
    e?.preventDefault();
    onClickAddResourceCategory(newCategoryName);
  };

  const onClickAddCategoryOnly = () => {
    setShowAddCategory(true);
    setErrMsg("");
  };

  const onClickAddCategoryOnlyCancel = () => {
    setNewCategoryName("");
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onUpdateNewCategoryName = (e: any) => {
    setNewCategoryName(e.target.value);
    setErrMsg("");
  };

  const onClickViewCategories = () => {
    setShowCategoryView(true);
    setShowAddResource(false);
    setErrMsg("");
  };

  const onClickBack = () => {
    setShowCategoryView(false);
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onClickAddResource = () => {
    setErrMsg("");
    setShowAddResource(true);
  };

  const onClickAddResourceCancel = () => {
    unsavedChanges.value = false;
    setErrMsg("");
    setShowAddResource(false);
    setOrgToSave(false);
    getResources();
  };

  const onClickFilterType = (type: string | null) => {
    setTypeFilter(type);
  };

  const onClickFilterResource = (selectedFilter: any) => {
    setCategoryFilter(categoryFilter === selectedFilter?.key ? null : selectedFilter?.key);
    setCategoryFilterName(selectedFilter?.name || null);
  };

  const onClickClearFilters = () => {
    setCategoryFilter(null);
    setCategoryFilterName(null);
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : showCategoryView ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col className="col-5">
              <h4 className="app-header-text">Resource Categories</h4>
            </Col>
            <Col className="text-end mb-3">
              <Button className="app-primary-color button me-3" onClick={onClickAddCategoryOnly} disabled={showAddCategory}>
                Add Category
              </Button>
              <Button className="app-primary-color button" onClick={onClickBack}>
                Back
              </Button>
            </Col>
          </Row>
          {showAddCategory && (
            <Form onSubmit={(e: any) => onClickSaveSubmitNewCategory(e)}>
              <Row className="mb-3">
                <Col xs={6}>
                  <Input
                    label="Name"
                    type="text"
                    value={newCategoryName}
                    onUpdate={(e: any) => onUpdateNewCategoryName(e)}
                    hasFocus={true}
                  />
                </Col>
                <Col xs={"auto"} className="d-flex align-items-center">
                  <Button type="submit" className="app-secondary-color button me-3" disabled={!newCategoryName}>
                    Add
                  </Button>
                  <div className="vr my-2" />
                  <Button className="app-primary-color button ms-3" onClick={onClickAddCategoryOnlyCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
          <Row>
            <Col xs={6}>
              {resourceCategories?.slice(1).length === 0 ? (
                <p className="mt-2">No Categories</p>
              ) : (
                resourceCategories
                  .slice(1)
                  .map((category: any) => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      getResourceCategories={getResourceCategories}
                      getResources={getResources}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col xs={3}>
              <h4 className="app-header-text">Resources</h4>
            </Col>
            <Col className="text-end">
              {!showCategoryView && (
                <>
                  <Button className="app-secondary-color button me-3" onClick={onClickViewCategories}>
                    Categories
                  </Button>
                  <Button className="app-primary-color button" onClick={() => onClickAddResource()}>
                    Create Resource
                  </Button>
                </>
              )}
            </Col>
          </Row>
          <Row className={categoryFilter ? "mb-3" : "mb-4"}>
            <Col className="d-flex justify-content-center gap-3">
              <Button
                className={!typeFilter ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onClickFilterType(null)}
              >
                All
              </Button>
              <Button
                className={typeFilter === "consumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onClickFilterType("consumable")}
              >
                Consumable
              </Button>
              <Button
                className={typeFilter === "nonconsumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => onClickFilterType("nonconsumable")}
              >
                Non-Consumable
              </Button>
              <div data-tooltip-id="tooltip-filter" data-tooltip-content={"Category Filter"}>
                <DropdownButton
                  id={categoryFilter ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                  title={<FontAwesomeIcon icon={faFilter} />}
                >
                  {resourceCategories?.map((cat: any) => (
                    <Dropdown.Item
                      key={cat.id}
                      href={`#/action-1`}
                      className={categoryFilter === cat.key ? "app-tertiary-color-inverse" : ""}
                      onClick={() => onClickFilterResource(cat)}
                    >
                      {cat.name}
                      {categoryFilter === cat.key ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </div>
              <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
            </Col>
          </Row>
          {categoryFilter && (
            <Row className="mb-3">
              <Col className="col-12 text-center">
                <Badge className="app-tertiary-color-inverse" bg="none">
                  {categoryFilterName}
                </Badge>
                <Badge className="app-tertiary-color ms-1 cursor-pointer" bg="none" onClick={onClickClearFilters}>
                  <FontAwesomeIcon icon={faClose}></FontAwesomeIcon>
                </Badge>
              </Col>
            </Row>
          )}
          <Row className="mb-3">
            <Col xs={12}>
              {showAddResource && (
                <AddResource
                  setOrgToSave={setOrgToSave}
                  typeFilter={typeFilter}
                  setTypeFilter={setTypeFilter}
                  categoryFilter={categoryFilter}
                  setCategoryFilter={setCategoryFilter}
                  setCategoryFilterName={setCategoryFilterName}
                  setShowAddResource={setShowAddResource}
                  getResources={getResources}
                  getAllResourceCategories={getResourceCategories}
                  setErrMsg={setErrMsg}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                />
              )}
              {typeFilter === "nonconsumable" && filteredNonConsumables.length === 0 ? (
                <p className="mt-2">No Non-Consumable Resources</p>
              ) : typeFilter === "consumable" && filteredConsumables.length === 0 ? (
                <p className="mt-2">No Consumable Resources</p>
              ) : typeFilter === "all" && filteredAll.length === 0 ? (
                <p className="mt-2">No Resources</p>
              ) : null}
              {!typeFilter && (
                <>
                  {filteredAll.map((resource: Resource, index: number) => (
                    <OrgResourceCard
                      key={index}
                      resource={resource}
                      getResources={getResources}
                      resourceCategories={resourceCategories}
                      onAddResourceCategory={onClickAddResourceCategory}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))}
                </>
              )}
              {typeFilter === "consumable" && (
                <>
                  {filteredConsumables.map((resource: Resource, index: number) => (
                    <OrgResourceCard
                      key={index}
                      resource={resource}
                      getResources={getResources}
                      resourceCategories={resourceCategories}
                      onAddResourceCategory={onClickAddResourceCategory}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))}
                </>
              )}
              {typeFilter === "nonconsumable" && (
                <>
                  {filteredNonConsumables.map((resource: Resource, index: number) => (
                    <OrgResourceCard
                      key={index}
                      resource={resource}
                      getResources={getResources}
                      resourceCategories={resourceCategories}
                      onAddResourceCategory={onClickAddResourceCategory}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))}
                </>
              )}
            </Col>
          </Row>
        </>
      )}
      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onClickAddResourceCancel} />
    </>
  );
};

export default OrgResources;
